// ** React Imports
import { useCallback, useState, useEffect, useRef } from "react";

// ** Custom Hooks
import useLocalStorageState from "hooks/useLocalStorageState";

// ##################################

const useVideoPlayer = ({
	videoRef,
	playerRef = null,
	options = {},
	isIos = false,
}) => {
	const {
		autoPlay: autoPlayOption,
		playsInline: playsInlineOption,
		playsInlineIos: playsInlineIosOption,
	} = options;

	const videoElement = videoRef?.current;
	const playerElement = playerRef?.current;

	const buffering = useRef(false);
	const paused = useRef(true);
	const playing = useRef(false);
	const ended = useRef(false);

	playing.current = !videoElement?.paused;
	paused.current = videoElement?.paused;
	ended.current = videoElement?.ended;

	// ** Zmienne Stanu
	const [muted, setMuted] = useLocalStorageState(false, "videoMute");
	const [played, _setPlayed] = useState(0);
	const setPlayed = (value) => {
		_setPlayed(Number(parseFloat(value)) || 0);
	};
	const [playedSeconds, _setPlayedSeconds] = useState(0);
	const setPlayedSeconds = (value) => {
		_setPlayedSeconds(parseFloat(value));
	};
	const [loaded, _setLoaded] = useState(0);
	const setLoaded = (value) => {
		_setLoaded(Number(parseFloat(value)) || 0);
	};
	const [duration, setDuration] = useState(0);

	const [volume, _setVolume] = useLocalStorageState(0.8, "videoVolume");
	const [fullscreen, setFullscreen] = useState(false);

	const setVolume = (value) => {
		_setVolume(parseFloat(value));
	};

	// ##################################

	// ** Funkcja uruchamia lub zatrzymuje odtwarzanie filmu
	const togglePlayPause = () => {
		if (videoElement) {
			if (playing.current) {
				videoElement.pause();
			} else {
				videoElement.play();
			}
		}
	};

	// ** Funkcja włacza lub wyłacza wyciszenie
	const toggleMuted = () => {
		setMuted((prevState) => !prevState);
	};

	// ** Funkcja obsługujaca zmianę głośności
	const changeVolume = (value) => {
		setVolume(value);
		setMuted(value === 0);
	};

	// ** Funkcja ustawiajaca parametr 'duration' (czas trwania)
	const handleOnLoadedData = () => {
		if (videoElement) {
			setDuration(videoElement.duration);
		}
	};

	// ** Funkcja ustawiajaca parametr 'playedSeconds'
	const handleOnTimeUpdate = () => {
		if (videoElement) {
			setPlayedSeconds(videoElement.currentTime);
			setPlayed(videoElement.currentTime / videoElement.duration);
		}
	};

	const handleOnProgress = () => {
		if (videoElement && videoElement.buffered?.length > 0) {
			// eslint-disable-next-line no-unsafe-optional-chaining
			setLoaded(videoElement.buffered?.end(0) / videoElement.duration);
		}
	};

	// ** Funkcja obsługujaca zmianę czasu odwarzania filmu po kliknięciu
	// ** w odpowiednie miejsce 'seekBaru'
	const handleSeekChange = (value) => {
		if (videoElement) {
			setPlayed(value);
			videoElement.currentTime = parseFloat(
				value * videoElement.duration
			);
		}
	};

	// ** Funkcja w zależności od wybranch opcji i urzadzenia na jakim
	// ** będzie odtwarzany film uruchamia odtwarzach w trybie 'inline' lub nie,
	const handlePlaysInline = useCallback(() => {
		if (isIos && playsInlineIosOption) {
			return true;
		}
		if (isIos && !playsInlineIosOption) {
			return false;
		}
		if (playsInlineOption) {
			return true;
		}
		return false;
	}, [isIos, playsInlineIosOption, playsInlineIosOption]);

	// ** Funkcja włacza lub wyłacza tryb pełnego ekranu 'Fullscreen'
	const toggleFullscreen = () => {
		/* MARCIN_P: DZ-1944 - Sprawdzanie czy jest fullscreen i wymuszenie zmiany stanu, ponieważ był bug przy zamykaniu fullscreena "Escape" */
		document.onfullscreenchange = () => {
			if (document.fullscreenElement === null) setFullscreen(false);
		};

		const {
			fullscreenElement,
			webkitFullscreenElement,
			mozFullScreenElement,
			msFullscreenElement,
			fullscreenEnabled,
			webkitFullscreenEnabled,
			mozFullScreenEnabled,
			msFullscreenEnabled,
		} = document;

		// ** Sprawdzenie czy obecnie pełny ekran jest właczony
		if (
			fullscreen &&
			(fullscreenElement ||
				webkitFullscreenElement ||
				mozFullScreenElement ||
				msFullscreenElement)
		) {
			const {
				exitFullscreen,
				webkitExitFullscreen,
				mozCancelFullScreen,
				msExitFullscreen,
			} = document;

			// Mechanizm wyłaczania pełnego ekranu w zależności od przegladarki
			if (exitFullscreen) {
				document.exitFullscreen();
				setFullscreen(false);
			} else if (webkitExitFullscreen) {
				document.webkitExitFullscreen();
				setFullscreen(false);
			} else if (mozCancelFullScreen) {
				document.mozCancelFullScreen();
				setFullscreen(false);
			} else if (msExitFullscreen) {
				document.msExitFullscreen();
				setFullscreen(false);
			}
		}
		// ** Jeśli obecnie pełny ekran nie jest włączony - włączenie pełnego ekranu
		else if (
			!fullscreen &&
			(fullscreenEnabled ||
				webkitFullscreenEnabled ||
				mozFullScreenEnabled ||
				msFullscreenEnabled)
		) {
			const {
				requestFullscreen,
				webkitRequestFullscreen,
				mozRequestFullScreen,
				msRequestFullscreen,
			} = playerElement || {};

			// ** Mechanizm właczania pełnego ekranu w zależności od przegladarki
			if (requestFullscreen) {
				playerElement?.requestFullscreen();
				setFullscreen(true);
			} else if (webkitRequestFullscreen) {
				playerElement?.webkitRequestFullscreen();
				setFullscreen(true);
			} else if (mozRequestFullScreen) {
				playerElement?.mozRequestFullScreen();
				setFullscreen(true);
			} else if (msRequestFullscreen) {
				playerElement?.msRequestFullscreen();
				setFullscreen(true);
			}
		}
	};

	// ##################################

	// ** Efekt w zależności od stanu 'volume' ustawia
	// ** głośność odtwarzania filmu
	useEffect(() => {
		if (videoElement) {
			videoElement.volume = volume;
		}
	}, [volume, videoElement]);

	useEffect(() => {
		if (videoElement) {
			const handleWaiting = () => {
				buffering.current = true;
			};

			const handlePlaying = () => {
				buffering.current = false;
			};

			videoElement.addEventListener("waiting", handleWaiting);
			videoElement.addEventListener("playing", handlePlaying);

			return () => {
				videoElement.removeEventListener("waiting", handleWaiting);
				videoElement.removeEventListener("playing", handlePlaying);
			};
		}
	}, [videoElement]);

	// ##################################

	return {
		playing: playing.current,
		autoPlay: autoPlayOption,
		ended: ended.current,
		togglePlayPause,
		muted,
		toggleMuted,
		fullscreen,
		toggleFullscreen,
		volume,
		changeVolume,
		duration,
		handleOnLoadedData,
		playedSeconds,
		handleOnTimeUpdate,
		played,
		loaded,
		handleSeekChange,
		handleOnProgress,
		handlePlaysInline,
	};
};

export default useVideoPlayer;
